import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Calendar,
  Tag,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  BookOpen,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import FAQAccordion from "../components/FAQAccordion";
import AdSensePlaceholder from "../components/AdSensePlaceholder";
import RelatedArticles from "../components/RelatedArticles";
import TableOfContents from "../components/TableOfContents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  administratif: "Administratif",
  finance: "Finance",
  sante: "Santé",
  auto: "Auto & Transport",
  famille: "Famille",
  "vie-pratique": "Vie Pratique",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-MA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/articles/${slug}`)
      .then((res) => setArticle(res.data))
      .catch(() => setError("Article introuvable."))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/4" />
          <div className="h-10 bg-slate-200 rounded w-3/4" />
          <div className="h-64 bg-slate-200 rounded-xl" />
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-slate-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="article-error"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center"
      >
        <p className="text-slate-500 text-lg">{error}</p>
        <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const { content } = article;
  const categoryLabel = CATEGORY_LABELS[article.category] || article.category;
  const canonicalUrl = `https://marocpratique.com/article/${article.slug}`;

  // Build FAQ JSON-LD
  const faqJsonLd =
    content.faq && content.faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: content.faq.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }
      : null;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.seoTitle,
    description: article.seoDescription,
    image: article.featured_image,
    datePublished: article.createdAt,
    dateModified: article.updatedAt,
    publisher: {
      "@type": "Organization",
      name: "MarocPratique",
      url: "https://marocpratique.com",
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://marocpratique.com" },
      { "@type": "ListItem", position: 2, name: categoryLabel, item: `https://marocpratique.com/categorie/${article.category}` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonicalUrl },
    ],
  };

  const combinedJsonLd = [
    articleJsonLd,
    breadcrumbJsonLd,
    ...(faqJsonLd ? [faqJsonLd] : []),
  ];

  return (
    <>
      <SEOHead
        title={article.seoTitle}
        description={article.seoDescription}
        canonical={canonicalUrl}
        image={article.featured_image}
        type="article"
        jsonLd={combinedJsonLd}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { label: categoryLabel, href: `/categorie/${article.category}` },
            { label: article.title, href: `/article/${article.slug}` },
          ]}
        />

        {/* Article Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pb-16">
          {/* Main Content */}
          <article
            data-testid="article-content"
            className="col-span-1 lg:col-span-8"
          >
            {/* Header */}
            <header className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/categorie/${article.category}`}
                  data-testid="article-category-badge"
                  className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors"
                >
                  {categoryLabel}
                </Link>
                {article.pillarPage && (
                  <Link
                    to={`/pilier/${article.pillarPage}`}
                    data-testid="article-pillar-badge"
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-200 transition-colors"
                  >
                    <BookOpen className="w-3 h-3" /> Guide
                  </Link>
                )}
              </div>

              <h1
                data-testid="article-title"
                className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight mb-4"
              >
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                {article.createdAt && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span data-testid="article-date">{formatDate(article.createdAt)}</span>
                  </div>
                )}
                {article.mainKeyword && (
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-4 h-4" />
                    <span>{article.mainKeyword}</span>
                  </div>
                )}
              </div>
            </header>

            {/* AdSense — After Intro */}
            <AdSensePlaceholder position="banner" />

            {/* Featured Image */}
            {article.featured_image && (
              <div className="rounded-xl overflow-hidden mb-8 aspect-video bg-slate-100">
                <img
                  src={article.featured_image}
                  alt={article.title}
                  data-testid="article-featured-image"
                  className="w-full h-full object-cover"
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            )}

            {/* Introduction */}
            {content.intro && (
              <section id="introduction" className="mb-8">
                <p
                  data-testid="article-intro"
                  className="text-base sm:text-lg leading-relaxed text-slate-700"
                >
                  {content.intro}
                </p>
              </section>
            )}

            {/* Steps */}
            {content.steps && content.steps.length > 0 && (
              <section id="etapes" className="mb-8">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-5 pb-3 border-b border-slate-200">
                  Etapes à suivre
                </h2>
                <ol
                  data-testid="article-steps"
                  className="space-y-3"
                >
                  {content.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold font-heading">
                        {idx + 1}
                      </span>
                      <p className="text-slate-700 text-base leading-relaxed pt-0.5">{step}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Price */}
            {content.price && (
              <section id="prix" className="mb-8">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-5 pb-3 border-b border-slate-200">
                  Prix et coûts
                </h2>
                <div
                  data-testid="article-price"
                  className="price-box flex items-start gap-3"
                >
                  <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-base leading-relaxed">{content.price}</p>
                </div>
              </section>
            )}

            {/* AdSense — Middle */}
            <AdSensePlaceholder position="rectangle" />

            {/* Delays */}
            {content.delays && (
              <section id="delais" className="mb-8">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-5 pb-3 border-b border-slate-200">
                  Délais
                </h2>
                <div
                  data-testid="article-delays"
                  className="delay-box flex items-start gap-3"
                >
                  <Clock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-base leading-relaxed">{content.delays}</p>
                </div>
              </section>
            )}

            {/* Errors */}
            {content.errors && content.errors.length > 0 && (
              <section id="erreurs" className="mb-8">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-5 pb-3 border-b border-slate-200">
                  Erreurs à éviter
                </h2>
                <ul
                  data-testid="article-errors"
                  className="space-y-2 bg-red-50 border border-red-100 rounded-xl p-5"
                >
                  {content.errors.map((err, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{err}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* FAQ */}
            {content.faq && content.faq.length > 0 && (
              <section id="faq" className="mb-8">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-5 pb-3 border-b border-slate-200">
                  Questions fréquentes (FAQ)
                </h2>
                <FAQAccordion faq={content.faq} />
              </section>
            )}

            {/* AdSense — End */}
            <AdSensePlaceholder position="rectangle" />

            {/* Related Articles */}
            {article.relatedArticlesData && article.relatedArticlesData.length > 0 && (
              <RelatedArticles articles={article.relatedArticlesData} />
            )}
          </article>

          {/* Sidebar */}
          <aside
            data-testid="article-sidebar"
            className="col-span-1 lg:col-span-4 space-y-6 lg:sticky lg:top-24 h-fit"
          >
            <TableOfContents content={content} />
            <AdSensePlaceholder position="sidebar" />
          </aside>
        </div>
      </div>
    </>
  );
}
